import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import { AiOutlineClose } from "react-icons/ai";

type Props = {
  sourceFile: File;
  onConfirm: (file: File) => void | Promise<void>;
  onClose: () => void;
  onProcessingChange?: (processing: boolean) => void;
};

const ProfileImageCropModal: React.FC<Props> = ({
  sourceFile,
  onConfirm,
  onClose,
  onProcessingChange,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageLoadError, setImageLoadError] = useState<string | null>(null);
  const [cropError, setCropError] = useState<string | null>(null);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const processingRef = useRef(false);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setIsImageLoading(true);
    setImageLoadError(null);
    setCropError(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    processingRef.current = false;
    setIsProcessing(false);

    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }

    const objectUrl = URL.createObjectURL(sourceFile);
    objectUrlRef.current = objectUrl;
    setImageSrc(objectUrl);

    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [sourceFile]);

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const image = new Image();

      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Unable to load selected image"));

      image.src = src;
    });
  };

  const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Unable to crop image."));
          }
        },
        "image/jpeg",
        0.8,
      );
    });
  };

  const withTimeout = <T,>(
    promise: Promise<T>,
    timeoutMs: number,
    message: string,
  ): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        reject(new Error(message));
      }, timeoutMs);

      promise
        .then((result) => {
          window.clearTimeout(timeoutId);
          resolve(result);
        })
        .catch((error) => {
          window.clearTimeout(timeoutId);
          reject(error);
        });
    });
  };

  const createCroppedImage = async () => {
    if (processingRef.current || !imageSrc || !croppedAreaPixels) {
      return;
    }

    processingRef.current = true;
    setIsProcessing(true);
    setCropError(null);
    onProcessingChange?.(true);

    try {
      const image = await withTimeout(
        loadImage(imageSrc),
        15000,
        "Image loading timed out.",
      );

      const cropWidth = Math.max(1, Math.round(croppedAreaPixels.width));
      const cropHeight = Math.max(1, Math.round(croppedAreaPixels.height));

      const MAX_OUTPUT_SIZE = 1024;

      const scale = Math.min(
        1,
        MAX_OUTPUT_SIZE / Math.max(cropWidth, cropHeight),
      );

      const outputWidth = Math.max(1, Math.round(cropWidth * scale));
      const outputHeight = Math.max(1, Math.round(cropHeight * scale));

      const canvas = document.createElement("canvas");
      canvas.width = outputWidth;
      canvas.height = outputHeight;

      const ctx = canvas.getContext("2d", {
        alpha: false,
      });

      if (!ctx) {
        throw new Error("Canvas is not supported.");
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        outputWidth,
        outputHeight,
      );

      const blob = await withTimeout(
        canvasToBlob(canvas),
        15000,
        "Image processing timed out.",
      );

      const croppedFile = new File([blob], `profile-${Date.now()}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });

      // await withTimeout(
      //   Promise.resolve(onConfirm(croppedFile)),
      //   30000,
      //   "Image upload timed out. Please check your internet connection.",
      // );
      await Promise.resolve(onConfirm(croppedFile));
    } catch (error) {
      console.error("Image crop/upload failed:", error);

      setCropError(
        error instanceof Error
          ? error.message
          : "Unable to prepare this image. Please try again.",
      );
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
      onProcessingChange?.(false);
    }
  };

  const handleClose = () => {
    if (isProcessing) return;
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="relative w-full max-w-md rounded-xl bg-white p-4 shadow-xl">
        {!isProcessing && (
          <button
            type="button"
            onClick={handleClose}
            className="absolute right-3 top-3 z-20 text-gray-500 hover:text-red-500"
          >
            <AiOutlineClose size={22} />
          </button>
        )}

        <h2 className="mb-3 text-center text-lg font-bold">
          Upload & Crop Profile Image
        </h2>

        <div className="relative h-80 w-full overflow-hidden rounded bg-black">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              mediaProps={{
                onError: () => {
                  setIsImageLoading(false);
                  setImageLoadError("Unable to load this image.");
                },
              }}
              onMediaLoaded={() => {
                setIsImageLoading(false);
                setImageLoadError(null);
              }}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}

          {isImageLoading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-black/70 text-white">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-white/40 border-t-white" />

              <span className="mt-3 text-sm">Loading image...</span>
            </div>
          )}

          {isProcessing && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/70 text-white">
              <div className="h-9 w-9 animate-spin rounded-full border-4 border-white/40 border-t-white" />

              <span className="mt-3 text-sm font-medium">
                Preparing profile image...
              </span>
            </div>
          )}
        </div>

        {(imageLoadError || cropError) && (
          <p className="mt-3 text-sm text-red-500">
            {imageLoadError || cropError}
          </p>
        )}

        <div className="mt-4">
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            disabled={isProcessing || isImageLoading}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-full disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        <button
          type="button"
          onClick={createCroppedImage}
          disabled={
            isProcessing ||
            isImageLoading ||
            !croppedAreaPixels ||
            Boolean(imageLoadError)
          }
          className={`mt-4 w-full rounded-lg py-3 font-semibold text-white transition ${
            isProcessing ||
            isImageLoading ||
            !croppedAreaPixels ||
            imageLoadError
              ? "cursor-not-allowed bg-gray-400"
              : "bg-red-600 hover:bg-red-700"
          }`}
        >
          {isProcessing
            ? "Preparing image..."
            : isImageLoading
              ? "Loading image..."
              : "Confirm & Continue"}
        </button>
      </div>
    </div>
  );
};

export default ProfileImageCropModal;
